import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';
import { AuthService } from 'src/app/core/services/auth.service';
import * as _ from 'lodash-es';
import { combineLatest, Subject, takeUntil } from 'rxjs';
import Icons from 'src/app/core/enums/icons.enum';
import { UserLoginModel } from 'src/app/core/models/user-login.model';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import AuthType from 'src/app/core/enums/auth.type.enum';
import { AuthenticationEvent } from 'src/app/core/models/authentication-event.model';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { Application } from 'src/portaljs/constants/application';
import { PortalApp } from 'src/app/core/shared/portal-app';
import { AppAction } from 'src/portaljs/models/app-action';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { ClientStateService } from 'src/app/core/services/client-state.service';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { EnvironmentService } from 'src/app/core/services/environment.service';
import { TemplatePortal } from '@angular/cdk/portal';
import ImsRole from 'src/app/core/enums/ims-role.enum';
import { ScreenStateService } from 'src/app/core/services/screen-state.service';
import { ScreenState } from 'src/app/core/enums/sceen-state.enum';

@Component({
    selector: 'fss-portal-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    standalone: false
})
export class UserProfileComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  public icons = Icons;
  public isGuest = false;
  public isDestinyAdmin = false;
  public isShared: boolean = false;
  public patronName = '';
  public patronToolbarName = '';
  public patronLocation = '';
  public patronDistrict = '';
  public isInterestSurveyAvailable: boolean;
  public highlightProfile: boolean = true;
  public highlightInterestSurvey: boolean = false;
  public portal: TemplatePortal<any>;
  public readonly MOBILE_HEADER_TEXT = 'User Profile';

  @ViewChild('userProfile') public userProfile: TemplateRef<any>;
  @ViewChild('userMenu') userMenu: MatMenu;
  public accessToSettings: boolean = false;
  public isDistrictUser: boolean;
  public isSiteAdmin: boolean;
  public showKioskSetting: boolean = false;
  public isKioskEnabled: boolean = false;
  @ViewChild('userProfileTrigger') userProfileTrigger: MatMenuTrigger;

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  constructor(
    private screenStateService: ScreenStateService,
    private authService: AuthService,
    private clientStateService: ClientStateService,
    private portalStateService: PortalStateService,
    private toolbarService: ToolbarService,
    private envService: EnvironmentService,
    private _viewContainerRef: ViewContainerRef) {
  }

  public ngOnInit(): void {
    this.isKioskEnabled = this.envService.getKioskMode();
    this.screenStateService.screenState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        if (this.isUnEnabledForCurrentApp()) {
          if (this.userProfileTrigger && this.userProfileTrigger.menuOpen && state === ScreenState.Mobile) {
            this.userProfileTrigger.closeMenu();
            this.showInSidenav();
          }
          if (this.portal && state !== ScreenState.Mobile && this.toolbarService?.portal?.title === this.MOBILE_HEADER_TEXT) {
            this.portal = undefined;
            this.toolbarService.portal = undefined;
            setTimeout(() => this.userProfileTrigger.openMenu());
          }
        }
      });

    this.portalStateService.getUserProfileSelection$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((selection) => {
        this.highlightInterestSurvey = selection.isInterestSurveySelected;
        this.highlightProfile = selection.isUserProfileSelected;
      });

    this.portalStateService.getActiveApp$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.portalStateService.setUserProfileSelected(false);
        this.portalStateService.setInterestSurveySelected(false);
      });

    combineLatest([this.authService.getUserLogin$(), this.portalStateService.getSelectableApps$()])
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((resp: [ulm: UserLoginModel, apps: PortalApp[]]) => {
        const ulm: UserLoginModel = resp[0];
        if (ulm) {
          const apps: PortalApp[] = resp[1] ;
          this.isInterestSurveyAvailable = ulm.isEngageEnabled() && _.some(apps, ['app', Application.DD]);
          this.isShared = ulm.isSharedUser();
          this.isGuest = ulm.isGuestUser();
          this.isDestinyAdmin = ulm.isDestinyAdmin();
          this.isDistrictUser = ulm.isDistrictUser();
          this.isSiteAdmin = ulm.hasRole([ImsRole.Administrator]) && !!ulm.getSiteGuid();
          this.patronName = ulm.getPatronName();
          this.patronToolbarName = ulm.getPatronToolbarName();
          this.patronDistrict = ulm.getDistrictName();
          this.patronLocation = ulm.getSiteGuid() ?  ulm.getLocationName() : '';
          this.accessToSettings = this.envService.isFUN_ENABLED() && ulm.doesUserHaveAccessToSettings();
          this.showKioskSetting = ulm.isDestinyAdmin() || ulm.isDistrictUser() || ulm.hasRole([ImsRole.Administrator, ImsRole.Faculty, ImsRole.Staff]);
        }
      });
  }

  public isUnEnabledForCurrentApp() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }

  public showInSidenav() {
    this.portal = new TemplatePortal(this.userProfile, this._viewContainerRef);
    this.toolbarService.portal = {templatePortal: this.portal, title: this.MOBILE_HEADER_TEXT};
  }

  public openUserProfile() {
    if (this.isUnEnabledForCurrentApp() && this.isMobile) {
      this.showInSidenav();
    }

    setTimeout(() => {
      this.userMenu._allItems['_results'].forEach((item: MatMenuItem) => {
        if (item['_elementRef']?.nativeElement?.classList?.contains('item-highlight')) {
          item.focus();
        }
      });
    });
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public canSeeProfileOption() {
    if (_.some(this.portalStateService.getSelectableApps(), (pa) => pa.app === Application.DD)) { // This line from: S-70796
      return !this.isGuest && !this.isShared && !this.isDestinyAdmin && !this.authService.isDistrictUserNotAtSite();
    }
    return false;
  }

  public canSeeInterestSurveyOption() {
    return !this.isGuest && !this.isShared && !this.isDestinyAdmin && this.isInterestSurveyAvailable;
  }

  public logout() {
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;
    this.clientStateService.clearNotifications();
    this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.LOGOUT, undefined, 'user-profile-logout'));
  }

  public gotoProfile() {
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;
    const originalApp: PortalApp = this.portalStateService.getActiveApp();
    const collApp: PortalApp = this.portalStateService.getSelectableApp(Application.COLLECTIONS);
    const appAction: AppAction = {destination: Application.COLLECTIONS, source: originalApp.app, route: 'UserProfile'};
    this.portalStateService.setActiveApp(collApp, appAction);
    this.portalStateService.setUserProfileSelected(true);
  }

  public gotoInterestSurvey() {
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;
    const originalApp: PortalApp = this.portalStateService.getActiveApp();
    const ddApp: PortalApp = this.portalStateService.getSelectableApp(Application.DD);
    if (ddApp) {
      const appAction: AppAction = {destination: Application.DD, source: originalApp.app, route: 'InterestSurvey'};
      this.portalStateService.setActiveApp(ddApp, appAction);
      this.portalStateService.setInterestSurveySelected(true);
    }
  }

  public gotoSettingApp(option: string) {
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;
    const originalApp: PortalApp = this.portalStateService.getActiveApp();
    const settingsApp: PortalApp = this.portalStateService.getSelectableApp(Application.UNIFIED_SETTINGS);
    if (settingsApp) {
      const appAction: AppAction = {destination: Application.UNIFIED_SETTINGS, source: originalApp.app, route: option};
      this.portalStateService.setActiveApp(settingsApp, appAction);
    }
  }

  public getSettingsMenuOption(){
    return this.isDestinyAdmin ? 'District & Site Settings': 'Site Settings';
  }

  public closeSidenav() {
    this.toolbarService.closeSidenavOnMenuOpen();
  }

  public isMessageSenderEnabled() {
    return (!this.envService.isProductionBuild() && this.envService.isMessageSenderEnabled());
  }
  public isSecretsEnabled() {
    return (!this.envService.isProductionBuild() && this.envService.isSecretsEnabled());
  }
  public isThemeChangerEnabled() {
    return (!this.envService.isProductionBuild() && this.envService.isThemePickerEnabled());
  }

  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }
  
  public toggleKioskMode() {
    this.isKioskEnabled = !this.isKioskEnabled;
    this.envService.setKioskMode(this.isKioskEnabled);
  }

}
